import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { aboutApi, pagesApi } from "../services/api";
import { config } from "../config/config";
import mediaService from "../services/mediaService";
import { RichContent } from "../components/RichContent";
import HeroSection from "../components/HeroSection";

function AboutPage() {
  const [sections, setSections] = useState([]);
  const [whyChooseUsItems, setWhyChooseUsItems] = useState([]);
  const [aboutPage, setAboutPage] = useState(null);
  const [homepageAbout, setHomepageAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    setLoading(true);
    try {
      const [sectionsRes, whyChooseRes, pageRes, homepageAboutRes] =
        await Promise.all([
          aboutApi.getSections(),
          aboutApi.getWhyChooseUs(),
          pagesApi.getByPageType("about"),
          fetch(`${config.api.baseURL}/homepage/about`),
        ]);

      setSections(sectionsRes.data || []);
      setWhyChooseUsItems(whyChooseRes.data || []);
      const pageData = pageRes.data || null;
      if (
        pageData?.slider_images &&
        typeof pageData.slider_images === "string"
      ) {
        try {
          pageData.slider_images = JSON.parse(pageData.slider_images);
        } catch {
          pageData.slider_images = pageData.slider_images
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean);
        }
      }
      setAboutPage(pageData);
      if (homepageAboutRes.ok) {
        const homepageAboutData = await homepageAboutRes.json();
        setHomepageAbout(homepageAboutData || null);
      }
    } catch (error) {
      console.error("Error loading about page data:", error);
      // Set fallback data if API fails
      setFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const setFallbackData = () => {
    setSections([
      {
        id: 1,
        title: "Mission",
        icon: "🎯",
        content:
          "Empower individuals and organizations to achieve sustainable growth through innovative learning solutions.",
        section_type: "mission",
      },
      {
        id: 2,
        title: "Vision",
        icon: "👁️",
        content:
          "To be the leading catalyst for professional and organizational transformation in the region.",
        section_type: "vision",
      },
      {
        id: 3,
        title: "Values",
        icon: "❤️",
        content:
          "Integrity, Innovation, Excellence, Collaboration, and Impact-driven results.",
        section_type: "values",
      },
      {
        id: 4,
        title: "Founder's Story",
        icon: "👨‍💼",
        content:
          "Mohammad Abdul Matin Emon brings over 15 years of experience in healthcare technology and entrepreneurship. As the former Head of Growth at Doctorola and Head of Digital at Praava Health, he has been at the forefront of digital transformation in Bangladesh's healthcare sector.\n\nHis journey from corporate executive to entrepreneur taught him valuable lessons about leadership, innovation, and resilience. These experiences form the foundation of Innobig's approach to professional development.\n\nThrough Innobig, Mohammad shares practical insights and strategies that help professionals and businesses navigate challenges and achieve sustainable growth.",
        section_type: "founder",
      },
    ]);

    setWhyChooseUsItems([
      {
        id: 1,
        icon: "🎯",
        title: "Practical Approach",
        description: "Real-world strategies that deliver measurable results",
      },
      {
        id: 2,
        icon: "👥",
        title: "Expert Guidance",
        description: "Learn from industry leaders with proven track records",
      },
      {
        id: 3,
        icon: "🔄",
        title: "Personalized Solutions",
        description: "Tailored programs to meet your specific needs",
      },
      {
        id: 4,
        icon: "📈",
        title: "Proven Results",
        description: "Track record of transforming careers and businesses",
      },
      {
        id: 5,
        icon: "🏆",
        title: "Community Network",
        description: "Join a network of like-minded professionals",
      },
      {
        id: 6,
        icon: "💡",
        title: "Innovative Methods",
        description: "Cutting-edge learning methodologies and tools",
      },
    ]);
  };

  useEffect(() => {
    if (aboutPage?.use_slider && aboutPage?.slider_images?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % aboutPage.slider_images.length);
      }, aboutPage.slider_interval || 5000);
      return () => clearInterval(interval);
    }
  }, [aboutPage]);

  const missionSection = sections.find((s) => s.section_type === "mission");
  const founderSection = sections.find((s) => s.section_type === "founder");
  const founderContent =
    founderSection?.content || homepageAbout?.founder_story || "";
  const founderTitle = founderSection?.title || "Founder's Story";
  const founderIcon = founderSection?.icon || "";
  const founderImage = homepageAbout?.founder_image || "";
  const founderParagraphs = founderContent ? founderContent.split("\n\n") : [];
  const mvvSections = sections
    .filter((s) => ["mission", "vision", "values"].includes(s.section_type))
    .sort((a, b) => {
      const order = { mission: 1, vision: 2, values: 3 };
      return (order[a.section_type] || 999) - (order[b.section_type] || 999);
    });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <HeroSection
        title={
          aboutPage?.hero_headline ||
          aboutPage?.title ||
          "Become a better leader, transform your business"
        }
        subtitle={
          aboutPage?.hero_subheadline ||
          aboutPage?.meta_description ||
          missionSection?.content ||
          "Turn people into assets, drive growth with innovation"
        }
        heroImage={aboutPage?.hero_image}
        useSlider={!!aboutPage?.use_slider}
        sliderImages={aboutPage?.slider_images || []}
        currentSlide={currentSlide}
        onSlideChange={setCurrentSlide}
        ctaText={aboutPage?.cta_text}
        ctaHref={aboutPage?.cta_href||"/contact"}
      />

      {/* Mission/Vision/Values */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mvvSections.map((section) => (
              <div key={section.id} className="text-center">
                <div className="text-4xl mb-4">{section.icon}</div>
                <h3 className="text-xl font-bold mb-4">{section.title}</h3>
                <p className="text-gray-600">
                  <RichContent html={section.content} />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      {founderContent && (
        <section className="py-16 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-3xl font-bold mb-6">{founderTitle}</h2>
                <div className="space-y-4">
                  {founderParagraphs.map((para, idx) => (
                    <p key={idx} className="text-gray-600">
                      <RichContent html={para} />
                    </p>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2">
                {founderImage ? (
                  <img
                    src={
                      founderImage.startsWith("http")
                        ? founderImage
                        : mediaService.getFileUrl(founderImage)
                    }
                    alt="Founder"
                    className="h-96 w-full rounded-lg object-cover"
                  />
                ) : (
                  <div className="bg-gray-200 h-96 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      {founderIcon && (
                        <div className="text-8xl mb-4">{founderIcon}</div>
                      )}
                      <span className="text-gray-600 text-lg">
                        Mohammad Abdul Matin Emon
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Why Choose Innobig
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUsItems.map((item) => (
              <div
                key={item.id}
                className="border rounded-lg p-6 hover:shadow-md transition"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-4">
            Ready to Transform Your Career or Business?
          </h2>
          <p className="mb-8">
            Join hundreds of professionals who have accelerated their growth
            with Innobig.
          </p>
          <div className="space-x-4">
            <Link
              to="/contact"
              className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition inline-block"
            >
              Work with Us
            </Link>
            <Link
              to="/programs"
              className="border border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition inline-block"
            >
              Explore Programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
